import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X, CheckCircle2, ArrowRight, UploadCloud, ClipboardList, Megaphone,
  MessagesSquare, Sparkles, Wrench, TrendingUp, KeyRound, CalendarRange,
  ShieldCheck, BadgeCheck, FileText, CircleDollarSign,
} from 'lucide-react';
import VehicleImage from './VehicleImage';
import { Reveal } from './Reveal';
import { IMAGES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: ClipboardList, title: 'Professional Management', text: 'We handle the day-to-day operations.' },
  { icon: Megaphone, title: 'Marketing & Booking', text: 'We market your vehicle to qualified renters.' },
  { icon: MessagesSquare, title: 'Guest Communication', text: 'We handle inquiries, communication, and guest support.' },
  { icon: Sparkles, title: 'Cleaning & Turnaround', text: 'We take care of cleaning and vehicle preparation between bookings.' },
  { icon: Wrench, title: 'Maintenance Coordination', text: 'We help coordinate maintenance and address vehicle issues.' },
  { icon: TrendingUp, title: 'Revenue Focused', text: 'We optimize pricing and availability to maximize earning potential.' },
];

const OWNER_PERKS = [
  { icon: KeyRound, label: 'You Keep Ownership' },
  { icon: CalendarRange, label: 'Flexible Options' },
  { icon: ShieldCheck, label: 'Insured & Protected' },
  { icon: BadgeCheck, label: 'Professional Management' },
];

const BRANDS = ['BMW', 'Mercedes-Benz', 'Porsche', 'Audi', 'Cadillac', 'Lexus', 'Tesla', 'Range Rover'];

const FLOW = [
  { icon: FileText, title: 'Your Car Payment', text: 'The monthly cost of owning your vehicle.' },
  { icon: ShieldCheck, title: 'Help Cover Your Monthly Payment', text: 'Rental income can help offset what you pay each month.' },
  { icon: CircleDollarSign, title: 'Extra Money in Your Pocket', text: 'Earn additional income from an asset you already own.' },
];

const EMPTY = {
  full_name: '', phone: '', email: '', vehicle_year: '', vehicle_make: '',
  vehicle_model: '', mileage: '', vehicle_location: '', monthly_payment: '',
  financed_or_leased: '', additional_info: '',
};

const inputCls =
  'w-full border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm font-light text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold/70 focus:bg-white/[0.05]';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-gold/80">{label}</span>
    {children}
  </label>
);

const ApplicationModal = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = (e) => {
    const picked = Array.from(e.target.files).slice(0, 5);
    setFiles(picked);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const photos = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${API}/uploads`, fd);
        photos.push(data.path);
      }
      await axios.post(`${API}/partner-applications`, { ...form, photos });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or text us at 786-920-2715.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="partner-application-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/85 p-4 py-10 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="relative w-full max-w-2xl border border-white/10 bg-surface p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute left-0 top-0 h-[2px] w-24 bg-gold" />
            <button data-testid="partner-form-close" onClick={onClose} aria-label="Close form" className="absolute right-4 top-4 p-2 text-white/60 transition-colors hover:text-gold">
              <X className="h-5 w-5" />
            </button>
            {done ? (
              <div data-testid="partner-form-success" className="flex min-h-[360px] flex-col items-center justify-center text-center">
                <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1} />
                <h3 className="font-display mt-6 text-3xl tracking-tight text-white">Thank you!</h3>
                <p className="mt-4 max-w-md text-base font-light leading-relaxed text-white/60">
                  Your vehicle has been submitted for review. Our team will evaluate it and contact you if it qualifies for the ALAS Velocity partner program.
                </p>
              </div>
            ) : (
              <>
                <span className="text-[10px] font-medium uppercase tracking-[0.35em] text-gold">Partner Application</span>
                <h3 className="font-display mt-2 text-3xl tracking-tight text-white">See if your vehicle qualifies</h3>
                <form data-testid="partner-application-form" onSubmit={submit} className="mt-8 grid gap-5 sm:grid-cols-2">
                  <Field label="Full Name"><input data-testid="partner-name-input" required value={form.full_name} onChange={set('full_name')} placeholder="Your full name" className={inputCls} /></Field>
                  <Field label="Phone Number"><input data-testid="partner-phone-input" required type="tel" value={form.phone} onChange={set('phone')} placeholder="(305) 000-0000" className={inputCls} /></Field>
                  <Field label="Email"><input data-testid="partner-email-input" required type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" className={inputCls} /></Field>
                  <Field label="Vehicle Location"><input data-testid="partner-location-input" required value={form.vehicle_location} onChange={set('vehicle_location')} placeholder="City, neighborhood" className={inputCls} /></Field>
                  <Field label="Vehicle Year"><input data-testid="partner-year-input" required value={form.vehicle_year} onChange={set('vehicle_year')} placeholder="2023" maxLength={4} className={inputCls} /></Field>
                  <Field label="Vehicle Make"><input data-testid="partner-make-input" required value={form.vehicle_make} onChange={set('vehicle_make')} placeholder="BMW" className={inputCls} /></Field>
                  <Field label="Vehicle Model"><input data-testid="partner-model-input" required value={form.vehicle_model} onChange={set('vehicle_model')} placeholder="M4 Competition" className={inputCls} /></Field>
                  <Field label="Current Mileage"><input data-testid="partner-mileage-input" required value={form.mileage} onChange={set('mileage')} placeholder="12,500" className={inputCls} /></Field>
                  <Field label="Monthly Vehicle Payment"><input data-testid="partner-payment-input" value={form.monthly_payment} onChange={set('monthly_payment')} placeholder="$ (optional)" className={inputCls} /></Field>
                  <Field label="Financed or Leased?">
                    <select data-testid="partner-financed-select" required value={form.financed_or_leased} onChange={set('financed_or_leased')} className={`${inputCls} appearance-none`}>
                      <option value="" disabled className="bg-surface">Select one</option>
                      <option value="financed" className="bg-surface">Financed</option>
                      <option value="leased" className="bg-surface">Leased</option>
                      <option value="owned" className="bg-surface">Owned outright</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Upload Vehicle Photos (up to 5)">
                      <label data-testid="partner-photos-upload" className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-white/20 px-4 py-8 text-sm text-white/50 transition-colors duration-300 hover:border-gold/60 hover:text-gold">
                        <UploadCloud className="h-5 w-5" />
                        {files.length ? `${files.length} photo${files.length > 1 ? 's' : ''} selected` : 'Click to choose photos'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFiles} />
                      </label>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Additional Information">
                      <textarea data-testid="partner-info-textarea" rows={3} value={form.additional_info} onChange={set('additional_info')} placeholder="Trim, condition, availability, anything we should know…" className={`${inputCls} resize-none`} />
                    </Field>
                  </div>
                  {error && <p data-testid="partner-form-error" className="sm:col-span-2 text-sm text-red-400">{error}</p>}
                  <div className="sm:col-span-2">
                    <button
                      data-testid="partner-submit-button"
                      type="submit"
                      disabled={sending}
                      className="group flex w-full items-center justify-center gap-3 bg-gold py-5 text-xs font-semibold uppercase tracking-[0.25em] text-ink shadow-[0_0_25px_rgba(200,168,93,0.25)] transition-all duration-300 hover:bg-gold-bright disabled:opacity-60"
                    >
                      {sending ? 'Submitting…' : 'Submit Vehicle for Review'}
                      {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Partner = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="partner" data-testid="partner-section" className="scroll-mt-24 border-y border-white/5 bg-[#0a0a0a] py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs font-medium uppercase tracking-[0.35em] text-gold">Luxury Vehicle Management</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-5 text-4xl leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Your luxury car can <span className="italic text-gold">help pay for itself.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/65 sm:text-lg">
                We help luxury car owners turn their vehicles into income-producing assets — while <span className="text-gold">we handle the rest</span>.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <button
                data-testid="partner-cta-button"
                onClick={() => setOpen(true)}
                className="group mt-9 inline-flex items-center gap-3 bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink shadow-[0_0_25px_rgba(200,168,93,0.25)] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(200,168,93,0.4)]"
              >
                See If Your Vehicle Qualifies
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="group relative overflow-hidden border border-gold/20">
              <div className="aspect-[4/5]">
                <VehicleImage src={IMAGES.m4} alt="Black luxury performance vehicle managed by ALAS Velocity" label="PARTNER FLEET" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-xl italic text-white">You provide the car.</p>
                <p className="font-display text-xl italic text-gold">We handle the rest.</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 grid gap-px border border-gold/20 bg-gold/10 md:grid-cols-[1fr_auto_1fr_auto_1fr]" data-testid="partner-flow">
            {FLOW.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="contents">
                  <div className="flex flex-col items-center bg-[#0a0a0a] px-6 py-10 text-center">
                    <Icon className="h-8 w-8 text-gold" strokeWidth={1.25} />
                    <h3 className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-white">{s.title}</h3>
                    <p className="mt-2 max-w-[240px] text-sm font-light text-white/50">{s.text}</p>
                  </div>
                  {i < FLOW.length - 1 && (
                    <div className="hidden items-center bg-[#0a0a0a] px-2 md:flex">
                      <ArrowRight className="h-6 w-6 text-gold/60" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-center text-xs font-light text-white/35">
            Rental income may help cover your monthly payment and provide additional income. Earnings vary by vehicle, availability, and demand — no specific amount is guaranteed.
          </p>
        </Reveal>

        <div className="mt-24">
          <Reveal>
            <h3 className="font-display text-center text-3xl tracking-tight text-white sm:text-4xl">
              You provide the car. <span className="italic text-gold">We handle the rest.</span>
            </h3>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={(i % 3) * 0.08} className="h-full">
                  <div data-testid={`partner-benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`} className="group flex h-full flex-col bg-[#0a0a0a] p-8 transition-colors duration-500 hover:bg-surface">
                    <Icon className="h-7 w-7 text-gold transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.25} />
                    <h4 className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 group-hover:text-gold">{b.title}</h4>
                    <p className="mt-3 text-sm font-light leading-relaxed text-white/50">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="mt-24 border border-gold/20 bg-surface p-10 md:p-14">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <Reveal>
                <span className="text-xs font-medium uppercase tracking-[0.35em] text-gold">Have a luxury vehicle?</span>
                <h3 className="font-display mt-4 text-3xl tracking-tight text-white sm:text-4xl">
                  We are looking for quality vehicles <span className="italic text-gold">like yours.</span>
                </h3>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 flex flex-wrap gap-3">
                  {BRANDS.map((b) => (
                    <span key={b} data-testid={`partner-brand-${b.toLowerCase().replace(/\s+/g, '-')}`} className="border border-white/10 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-gold/50 hover:text-gold">
                      {b}
                    </span>
                  ))}
                  <span className="px-2 py-2.5 text-xs uppercase tracking-[0.2em] text-gold">and more.</span>
                </div>
                <p className="mt-5 text-xs font-light text-white/40">All vehicles are subject to qualification and approval.</p>
              </Reveal>
            </div>
            <div className="flex flex-col justify-center gap-5">
              {OWNER_PERKS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Reveal key={p.label} delay={i * 0.07}>
                    <div data-testid={`partner-perk-${p.label.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-4 border-b border-white/10 pb-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-gold/25">
                        <Icon className="h-5 w-5 text-gold" strokeWidth={1.25} />
                      </span>
                      <span className="text-sm font-semibold uppercase tracking-[0.18em] text-white">{p.label}</span>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 border-t border-gold/20 pt-10 text-center">
              <p className="font-display text-2xl tracking-tight text-white sm:text-3xl">Limited vehicles accepted.</p>
              <button
                data-testid="partner-cta-button-bottom"
                onClick={() => setOpen(true)}
                className="group mt-6 inline-flex items-center gap-3 bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-gold-bright"
              >
                Partner With Us and Start Earning
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      <ApplicationModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
};

export default Partner;
